import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import styles from './DBInputModal.module.css';
import PictureAutoUpload from './PictureAutoUpload';


// 이미지 임포트
import download_icon from "../../img/common/download_icon.svg";
import upload_icon from "../../img/common/upload_icon.svg";

// 폴더째 올리면 파일명으로 PDO Image / PDO Pathology 를 나누는 업로드 (2026-10-01)
const AUTO_PICTURE = 'PDO 이미지 (자동 분류)';

function DBInputModal(props) {
    const { t } = useTranslation();
    const [fileData, setFileData] = useState([]);
    const [selectedOption, setSelectedOption] = useState('Select');
    const [alertMsg, setAlertMsg] = useState({ message: '', type: 'danger' });
    const [imageFiles, setImageFiles] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [tmp, setTmp] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [pictureBusy, setPictureBusy] = useState(false);

    // 이미지 업로드 중에는 닫기 전에 한 번 묻는다
    const requestClose = () => {
        if (pictureBusy && !window.confirm(t('업로드가 진행 중입니다. 닫으면 남은 사진은 올라가지 않습니다. 닫을까요?'))) return;
        props.onClose();
    };

    // URL 매핑
    const apiUrlMap = {
        'Organoid summary': '/api/setOrganoidInfo',
        'Patient summary': '/api/setPatientInfo',
        '비공개용 Patient info(stomach)': '/api/setPrivatePatientInfo',
        'Pathology_Picture_picture_data': '/api/setPathologyPicturePictureData',
        'Light_Microscope_picture_data': '/api/setLightMicroscopePictureData',
        'Banking master stock': '/api/setBankingInfo',
        'IC50 Graph data': '/api/setGraphData',
    };

    // 날짜를 YYYY-MM-DD 로 포맷
    const formatDate = (date) => {
        // 원본 date를 변경하지 않도록 복제
        const newDate = new Date(date);
        newDate.setDate(newDate.getDate() + 1); // 하루 추가

        const Y = newDate.getFullYear();
        const M = String(newDate.getMonth() + 1).padStart(2, '0');
        const D = String(newDate.getDate()).padStart(2, '0');

        return `${Y}-${M}-${D}`;
    };


    const handleImageUpload = (e) => {
        const newFiles = Array.from(e.target.files);
        setImageFiles(prev => [...prev, ...newFiles]);
    };

    const removeSelectedFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleImageSubmit = async () => {
        setIsUploading(true);
        try {
            const apiUrl = apiUrlMap[selectedOption];
            const form = new FormData();
            imageFiles.forEach(f => form.append('images', f));
            const res = await axios.post(apiUrl, form, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data !== '') {
                const type = res.data === 'Success' ? 'success' : 'danger';
                setAlertMsg({ message: res.data, type });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsUploading(false);
        }
    };

    const processMergedCells = (ws) => {
        const merges = ws['!merges'] || [];
        merges.forEach(range => {
            const s = range.s, e = range.e;
            const startRef = XLSX.utils.encode_cell(s);
            const main = ws[startRef];
            if (!main) return;
            for (let R = s.r; R <= e.r; ++R) {
                for (let C = s.c; C <= e.c; ++C) {
                    const ref = XLSX.utils.encode_cell({ r: R, c: C });
                    ws[ref] = { v: main.v, t: main.t, w: main.w };
                }
            }
        });
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        const reader = new FileReader();
        reader.onload = evt => {
            const bstr = evt.target.result;
            const wb = XLSX.read(bstr, {
                type: 'binary',
                cellDates: true,
                raw: true
            });
            const ws = wb.Sheets[wb.SheetNames[0]];
            processMergedCells(ws);

            const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true });
            data.forEach(row => {
                row.forEach((cell, idx) => {
                    let v = cell;
                    if (cell instanceof Date) {
                        v = formatDate(cell);
                    } else if (typeof cell === 'string') {
                        if (/^\d{4}-\d{2}-\d{2}$/.test(cell)) {
                            // 이미 YYYY-MM-DD
                            v = cell;
                        } else if (/^\d{8}$/.test(cell)) {
                            // YYYYMMDD → YYYY-MM-DD 변환
                            v = `${cell.slice(0, 4)}-${cell.slice(4, 6)}-${cell.slice(6, 8)}`;
                        }
                    }
                    row[idx] = v;
                });
            });

            setFileData(data);
        };
        reader.readAsBinaryString(file);
    };

    const handleFilesUpload = (e) => {
        const files = Array.from(e.target.files);
        files.forEach(file => {
            const reader = new FileReader();
            reader.onload = evt => {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, {
                    type: 'binary',
                    cellDates: true,
                    raw: true
                });
                const ws = wb.Sheets[wb.SheetNames[0]];
                processMergedCells(ws);

                const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true });
                console.log(data);
                data.forEach(row => {
                    row.forEach((cell, idx) => {
                        let v = cell;

                        if (cell instanceof Date) {
                            // 진짜 Date 객체만 YYYY-MM-DD로
                            v = formatDate(cell);
                        } else if (typeof cell === 'string') {
                            // 문자열이 날짜 포맷일 때만 변환 (M/D/Y 또는 YYYYMMDD)
                            if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(cell)) {
                                const [m, d, yRaw] = cell.split('/');
                                const y = yRaw.length === 2 ? '20' + yRaw : yRaw;
                                v = formatDate(new Date(Number(y), Number(m) - 1, Number(d)));
                            } else if (/^\d{8}$/.test(cell)) {
                                v = `${cell.slice(0, 4)}-${cell.slice(4, 6)}-${cell.slice(6, 8)}`;
                            }
                        }
                        row[idx] = v;
                    });
                });

                setSelectedFiles(prev => [...prev, { fileName: file.name, data }]);
            };
            reader.readAsBinaryString(file);
        });
    };

    const handleSubmit = async () => {
        try {
            const apiUrl = apiUrlMap[selectedOption];

            if (selectedOption === 'IC50 Graph data') {
                setIsUploading(true);
                const results = await Promise.all(
                    selectedFiles.map(async (file) => {
                        const res = await axios.post(apiUrl, {
                            fileName: file.fileName,
                            data: file.data,     // 2차원 배열 그대로 JSON으로
                        });
                        return res.data;
                    })
                );
                const ok = results.every((r) => r === 'Success');
                setAlertMsg({
                    message: ok ? 'All files uploaded successfully' : 'Some files failed to upload',
                    type: ok ? 'success' : 'danger',
                });
                setIsUploading(false);
                return;
            }

            // 그 외 케이스는 기존 로직 유지
            if (selectedOption === 'Banking master stock') {
                const res = await axios.post(apiUrl, { data: fileData });
                if (res.data !== '') {
                    const type = res.data === 'Success' ? 'success' : 'danger';
                    setAlertMsg({ message: res.data, type });
                }
            } else {
                const res = await axios.post(apiUrl, { data: fileData });
                if (res.data !== '') {
                    const type = res.data === 'Success' ? 'success' : 'danger';
                    setAlertMsg({ message: res.data, type });
                }
            }
        } catch (err) {
            console.error(err);
            setIsUploading(false);
        }
    };

    const handleSelectChange = (e) => setSelectedOption(e.target.value);

    const handleDownload = () => {
        const fileMap = {
            'Organoid summary': 'OrganoidSummary.xlsx',
            'Patient summary': 'PatientSummary.xlsx',
            'Banking master stock': 'banking_form.xlsx',
            'IC50 Graph data': 'graphData_form.xlsx'
        };
        const fileName = fileMap[selectedOption];
        if (fileName) {
            const link = document.createElement('a');
            link.href = process.env.PUBLIC_URL + '/files/' + fileName;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } else {
            alert('엑셀 파일 양식을 지원하지 않습니다.');
        }
    };

    return (
        <div className={styles.modalBackground} onClick={e => e.target === e.currentTarget && requestClose()}>
            <div className={`${styles.modalContainer} ${selectedOption === AUTO_PICTURE ? styles.modalWide : ''}`} onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div className={styles.modalHeaderName}>{t('DB Input')}</div>
                    <div onClick={requestClose} className={styles.closeButton}>X</div>
                </div>
                {alertMsg.message && (
                    <Alert
                        variant={alertMsg.type}
                        onClose={() => setAlertMsg({ message: '', type: 'danger' })}
                        dismissible
                        className={styles.error}
                    >
                        <pre>{alertMsg.message}</pre>
                    </Alert>
                )}
                <div>
                    <div className={styles.selectBox}>
                        <select value={selectedOption} onChange={handleSelectChange} disabled={pictureBusy}>
                            <option>Select</option>
                            <option value={AUTO_PICTURE}>{t(AUTO_PICTURE)}</option>
                            <option>Organoid summary</option>
                            <option>Patient summary</option>
                            <option>비공개용 Patient info(stomach)</option>
                            <option>Pathology_Picture_picture_data</option>
                            <option>Light_Microscope_picture_data</option>
                            <option>Banking master stock</option>
                            <option>IC50 Graph data</option>
                        </select>
                    </div>

                    {selectedOption === AUTO_PICTURE ? (
                        <PictureAutoUpload onBusyChange={setPictureBusy} />
                    ) : (selectedOption === 'Pathology_Picture_picture_data' ||
                        selectedOption === 'Light_Microscope_picture_data') ? (
                        <>
                            <input type="file" accept="image/*" multiple onChange={handleImageUpload} />
                            <button onClick={handleImageSubmit}>
                                <img src={upload_icon} alt='' />&nbsp;{t('Upload Images')}
                            </button>
                            {isUploading && <div>{t('업로드 중입니다. 잠시만 기다려주세요...')}</div>}
                            <div className={styles.imageList}>
                                {imageFiles.map((file, i) => (
                                    <div key={i}>
                                        {file.name} <button onClick={() => removeSelectedFile(i)}>x</button>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : selectedOption === 'Select' ? (
                        <div>{t('일괄 입력하실 데이터 카테고리를 선택해주세요.')}</div>
                    ) : selectedOption === 'IC50 Graph data' ? (
                        <>
                            <input
                                type="file"
                                accept=".xlsx, .xls"
                                multiple
                                className={styles.fileInput}
                                onChange={handleFilesUpload}
                            />
                            <button onClick={handleDownload}>
                                <img src={download_icon} alt='' />&nbsp;{t('엑셀 폼 다운로드')}
                            </button>
                            <button onClick={handleSubmit}>
                                <img src={upload_icon} alt='' />&nbsp;{t('Upload')}
                            </button>
                            {isUploading && <div>{t('업로드 중입니다. 잠시만 기다려주세요...')}</div>}
                            <div className={styles.fileList}>
                                {selectedFiles.map((file, i) => (
                                    <div key={i}>
                                        {file.fileName} <button onClick={() => removeSelectedFile(i)}>x</button>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <>
                            <input
                                type="file"
                                accept=".xlsx, .xls"
                                className={styles.fileInput}
                                onChange={handleFileUpload}
                            />
                            <button onClick={handleDownload}>
                                <img src={download_icon} alt='' />&nbsp;{t('엑셀 폼 다운로드')}
                            </button>
                            <button onClick={handleSubmit}>
                                <img src={upload_icon} alt='' />&nbsp;{t('Upload')}
                            </button>
                            {isUploading && <div>{t('업로드 중입니다. 잠시만 기다려주세요...')}</div>}
                        </>
                    )}
                </div>
                <div>{tmp}</div>
            </div>
        </div>
    );
}

export default DBInputModal;
